import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { useTrainingResults } from "@/hooks/putting/useTrainingResults";
import PuttingHistoryTabs from "@/components/putting/training/PuttingHistoryTabs";
import MultiTestResultsChart from "@/components/putting/training/MultiTestResultsChart";
import NavigationMenu from "@/components/layout/NavigationMenu";
import { Button } from "@/components/ui/button";

const WedgingTraining = () => {
  const { t } = useTranslation('common');
  // State pour suivre le niveau sélectionné
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  
  // Utilisation du hook spécifique pour les entraînements
  const { 
    trainingResults, 
    isLoading, 
    chartDataByTest,
    testTypes,
    level1Results, 
    level2Results, 
    level3Results 
  } = useTrainingResults();

  // Filtrer uniquement les résultats liés au wedging
  const wedgingTestTypes = testTypes.filter(type => 
    type.toLowerCase().includes('wedging') ||
    type.toLowerCase().includes('mi-distance')
  );
  
  const wedgingResults = trainingResults.filter(result => {
    const testName = (result.test_name || '').toLowerCase();
    const testId = (result.test_id || '').toLowerCase();
    return testName.includes('wedging') || 
           testId.includes('wedging');
  });
  
  // Filtrer les résultats de wedging par niveau
  const wedgingLevel1Results = level1Results.filter(result => {
    const testName = (result.test_name || '').toLowerCase();
    const testId = (result.test_id || '').toLowerCase();
    return testName.includes('wedging') || 
           testId.includes('wedging');
  });
  
  const wedgingLevel2Results = level2Results.filter(result => {
    const testName = (result.test_name || '').toLowerCase();
    const testId = (result.test_id || '').toLowerCase();
    return testName.includes('wedging') || 
           testId.includes('wedging');
  });
  
  const wedgingLevel3Results = level3Results.filter(result => {
    const testName = (result.test_name || '').toLowerCase();
    const testId = (result.test_id || '').toLowerCase();
    return testName.includes('wedging') || 
           testId.includes('wedging');
  });

  // Gérer le changement de niveau dans les tabs
  const handleLevelChange = (level: string | null) => {
    setSelectedLevel(level);
  };

  // Préparer les données filtrées par type de test pour le graphique
  const filteredChartDataByTest: Record<string, any> = {};
  wedgingTestTypes.forEach(testType => {
    if (chartDataByTest[testType]) {
      filteredChartDataByTest[testType] = chartDataByTest[testType];
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      <div className="absolute top-20 left-6">
        <Button 
          variant="ghost" 
          className="text-golf-600 hover:text-golf-700 flex items-center gap-2" 
          asChild
        >
          <Link to="/training">
            <ChevronLeft className="h-5 w-5" />
            {t('wedgingTraining.backToTraining')}
          </Link>
        </Button>
      </div>
      <div className="container mx-auto py-8 px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-golf-800 mb-4">{t('wedgingTraining.title')}</h1>
          <p className="text-xl text-golf-600">{t('wedgingTraining.subtitle')}</p>
        </motion.div>
        
        <div className="my-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Results Chart - Now in the left wider column */}
          <div className="lg:col-span-2">
            <MultiTestResultsChart 
              chartDataByTest={filteredChartDataByTest}
              testTypes={wedgingTestTypes}
              selectedLevel={selectedLevel}
              trainingResults={wedgingResults}
            />
          </div>
          
          {/* Results History - Now in the right narrower column */}
          <div className="lg:col-span-1">
            <PuttingHistoryTabs 
              puttingResults={wedgingResults}
              level1Results={wedgingLevel1Results}
              level2Results={wedgingLevel2Results}
              level3Results={wedgingLevel3Results}
              onLevelChange={handleLevelChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WedgingTraining;
